import { lazy, Suspense, useEffect, useState, type ReactNode, Fragment } from 'react'
import { Navigate, Outlet, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '@/providers/auth'
import { PermissionsProvider, usePermission } from '@/providers/permissions'
import { useMyAccess } from '@/hooks/useMyAccess'
import { ADMIN_HOME, resolveSessionTarget } from '@/lib/access'
import { fetchCompanyData } from '@/lib/companies'
import { clearCompanyId, getAccessToken, getCompanyId } from '@/lib/session'
import { ONBOARDING_PATH } from '@/lib/onboarding'
import { DEFAULT_SITE_SECTION } from '@/lib/siteSettings'
import { DASHBOARD_ROOT, allowedDashboards, landingDashboard, readLastDashboard } from '@/lib/dashboards'
import { AccessDenied, PermissionGate } from '@/components/PermissionGate'
import { FullscreenLoader, PageLoader } from '@/components/common/Loaders'
import { AppLayout } from '@/components/layout/AppLayout'
import { NotFound } from '@/pages/NotFound'
import { ErrorState } from '@/components/common/ErrorState'
import { errorStatus } from '@/lib/errors'

const Login = lazy(() => import('@/pages/Login').then(m => ({ default: m.Login })))
const GoogleCallback = lazy(() => import('@/pages/Login/GoogleCallback').then(m => ({ default: m.GoogleCallback })))
const Register = lazy(() => import('@/pages/Register').then(m => ({ default: m.Register })))
const ForgotPassword = lazy(() => import('@/pages/ForgotPassword').then(m => ({ default: m.ForgotPassword })))
const RecoverPassword = lazy(() => import('@/pages/RecoverPassword').then(m => ({ default: m.RecoverPassword })))
const Unsubscribe = lazy(() => import('@/pages/Unsubscribe').then(m => ({ default: m.Unsubscribe })))
const InitialSetup = lazy(() => import('@/pages/Welcome').then(m => ({ default: m.InitialSetup })))
const NewCompany = lazy(() => import('@/pages/Welcome/NewCompany').then(m => ({ default: m.NewCompany })))
const AcceptInvites = lazy(() => import('@/pages/Welcome/AcceptInvites').then(m => ({ default: m.AcceptInvites })))
const Onboarding = lazy(() => import('@/pages/Onboarding').then(m => ({ default: m.Onboarding })))
const NoDashboard = lazy(() => import('@/pages/Dashboard').then(m => ({ default: m.NoDashboard })))
const SiteDashboard = lazy(() => import('@/pages/Dashboard/Site').then(m => ({ default: m.SiteDashboard })))
const SalesDashboard = lazy(() => import('@/pages/Dashboard/Vendas').then(m => ({ default: m.SalesDashboard })))
const FinanceDashboard = lazy(() => import('@/pages/Dashboard/Financeiro').then(m => ({ default: m.FinanceDashboard })))
const SalesReportsRedirect = lazy(() => import('@/pages/Dashboard/Vendas/ReportsRedirect').then(m => ({ default: m.SalesReportsRedirect })))

const RealState = lazy(() => import('@/pages/RealState/ListRealState').then(m => ({ default: m.RealState })))
const GetRealState = lazy(() => import('@/pages/RealState/GetRealState').then(m => ({ default: m.GetRealState })))
const CreateRealState = lazy(() => import('@/pages/RealState/CreateRealState').then(m => ({ default: m.CreateRealState })))
const EditRealState = lazy(() => import('@/pages/RealState/EditRealState').then(m => ({ default: m.EditRealState })))
const Owners = lazy(() => import('@/pages/RealState/Owners').then(m => ({ default: m.Owners })))
const PropertyKeys = lazy(() => import('@/pages/RealState/Keys').then(m => ({ default: m.PropertyKeys })))
const GetOwner = lazy(() => import('@/pages/RealState/Owners/GetOwner').then(m => ({ default: m.GetOwner })))
const OwnerForm = lazy(() => import('@/pages/RealState/Owners/OwnerForm').then(m => ({ default: m.OwnerForm })))
const ListingIntakes = lazy(() => import('@/pages/RealState/ListingIntakes').then(m => ({ default: m.ListingIntakes })))
const GetListingIntake = lazy(() => import('@/pages/RealState/ListingIntakes/GetListingIntake').then(m => ({ default: m.GetListingIntake })))
const ListRentContracts = lazy(() => import('@/pages/RealState/RentContracts/ListRentContracts').then(m => ({ default: m.ListRentContracts })))
const RentContractForm = lazy(() => import('@/pages/RealState/RentContracts/RentContractForm').then(m => ({ default: m.RentContractForm })))
const RentContractWizard = lazy(() => import('@/pages/RealState/RentContracts/RentContractWizard').then(m => ({ default: m.RentContractWizard })))
const Financeiro = lazy(() => import('@/pages/Financeiro').then(m => ({ default: m.Financeiro })))
const GetPayment = lazy(() => import('@/pages/Financeiro/GetPayment').then(m => ({ default: m.GetPayment })))
const OnlineBilling = lazy(() => import('@/pages/Financeiro/Recebimentos').then(m => ({ default: m.OnlineBilling })))

const ListCustomers = lazy(() => import('@/pages/Customers/ListCustomers').then(m => ({ default: m.ListCustomers })))
const GetCustomer = lazy(() => import('@/pages/Customers/GetCustomer').then(m => ({ default: m.GetCustomer })))
const CreateCustomer = lazy(() => import('@/pages/Customers/CreateCustomer').then(m => ({ default: m.CreateCustomer })))
const EditCustomer = lazy(() => import('@/pages/Customers/EditCustomer').then(m => ({ default: m.EditCustomer })))

const SalesFunnelBoard = lazy(() => import('@/pages/SalesFunnel/Board').then(m => ({ default: m.SalesFunnelBoard })))
const SalesFunnelLeadDetails = lazy(() => import('@/pages/SalesFunnel/LeadDetails').then(m => ({ default: m.SalesFunnelLeadDetails })))
const SalesFunnelSettings = lazy(() => import('@/pages/SalesFunnel/Settings').then(m => ({ default: m.SalesFunnelSettings })))
const ChatPage = lazy(() => import('@/pages/Chat').then(m => ({ default: m.ChatPage })))

const Missions = lazy(() => import('@/pages/Missions').then(m => ({ default: m.Missions })))
const StudioVideos = lazy(() => import('@/pages/Studio').then(m => ({ default: m.StudioVideos })))
const NewStudioVideo = lazy(() => import('@/pages/Studio/NewVideo').then(m => ({ default: m.NewStudioVideo })))
const ListBlogPosts = lazy(() => import('@/pages/Blog/ListPosts').then(m => ({ default: m.ListBlogPosts })))
const EditBlogPost = lazy(() => import('@/pages/Blog/EditPost').then(m => ({ default: m.EditBlogPost })))
const DocumentModels = lazy(() => import('@/pages/Documents/Models').then(m => ({ default: m.DocumentModels })))
const EditDocumentModel = lazy(() => import('@/pages/Documents/Models/EditModel').then(m => ({ default: m.EditDocumentModel })))
const NewDocumentModel = lazy(() => import('@/pages/Documents/Models/NewModel').then(m => ({ default: m.NewDocumentModel })))
const PreviewModel = lazy(() => import('@/pages/Documents/Models/PreviewModel').then(m => ({ default: m.PreviewModel })))

const ListCompanies = lazy(() => import('@/pages/Companies/ListCompanies').then(m => ({ default: m.ListCompanies })))
const CompanySettingsLayout = lazy(() =>
  import('@/pages/Companies/GetCompany/CompanySettingsLayout').then(m => ({ default: m.CompanySettingsLayout }))
)
const GeneralSettings = lazy(() => import('@/pages/Companies/GetCompany/GeneralSettings').then(m => ({ default: m.GeneralSettings })))
const Watermark = lazy(() => import('@/pages/Companies/GetCompany/GeneralSettings/Watermark').then(m => ({ default: m.Watermark })))
const SiteSettings = lazy(() => import('@/pages/Companies/GetCompany/WebsiteSettings').then(m => ({ default: m.SiteSettings })))
const Team = lazy(() => import('@/pages/Companies/GetCompany/Team').then(m => ({ default: m.Team })))
const Activity = lazy(() => import('@/pages/Companies/GetCompany/Activity').then(m => ({ default: m.Activity })))
const Integrations = lazy(() => import('@/pages/Companies/GetCompany/Integrations').then(m => ({ default: m.Integrations })))
const InstagramSettings = lazy(() =>
  import('@/pages/Companies/GetCompany/Integrations/Instagram/components/Settings').then(m => ({ default: m.Settings }))
)
const OlxSettings = lazy(() => import('@/pages/Companies/GetCompany/Integrations/OLX').then(m => ({ default: m.OlxSettings })))
const WhatsAppSettings = lazy(() =>
  import('@/pages/Companies/GetCompany/Integrations/WhatsApp/components/Settings').then(m => ({ default: m.WhatsAppSettings }))
)
const AttendantSettings = lazy(() => import('@/pages/Companies/GetCompany/AttendantSettings').then(m => ({ default: m.AttendantSettings })))
const Subscriptions = lazy(() => import('@/pages/Companies/GetCompany/SubscriptionSettings').then(m => ({ default: m.Subscriptions })))

const GetUser = lazy(() => import('@/pages/User/GetUser').then(m => ({ default: m.GetUser })))
const UserInvites = lazy(() => import('@/pages/User/Invites').then(m => ({ default: m.UserInvites })))
const InstagramOAuth = lazy(() => import('@/pages/OAuth/Instagram').then(m => ({ default: m.InstagramOAuth })))

const AdminCompanies = lazy(() => import('@/pages/Admin/Companies').then(m => ({ default: m.AdminCompanies })))
const AdminSchedules = lazy(() => import('@/pages/Admin/Schedules').then(m => ({ default: m.AdminSchedules })))
const AdminHealth = lazy(() => import('@/pages/Admin/Health').then(m => ({ default: m.AdminHealth })))
const AdminSales = lazy(() => import('@/pages/Admin/Sales').then(m => ({ default: m.AdminSales })))
const AdminAi = lazy(() => import('@/pages/Admin/Ai').then(m => ({ default: m.AdminAi })))

const GONE_COMPANY_STATUSES = [400, 403, 404]

function RequireSession() {
  const location = useLocation()
  const navigate = useNavigate()
  const { user, company, signIn, signOut } = useAuth()
  const token = getAccessToken()
  const { data: access, isError, error, refetch } = useMyAccess()
  const [loadingCompanyId, setLoadingCompanyId] = useState<string | null>(null)
  const [companyError, setCompanyError] = useState<unknown>(null)
  const adminWithoutCompany = !company.id && !!access && resolveSessionTarget(access, getCompanyId()).kind === 'adminHome'
  const ready = !!company.id || adminWithoutCompany
  const sessionEnded = isError && (!getAccessToken() || errorStatus(error) === 401)

  useEffect(() => {
    if (sessionEnded) {
      signOut(() => navigate('/login', { replace: true }))
      return
    }
    if (isError || companyError || !access || company.id) return
    const sessionUser = user.id ? user : { ...user, id: access.userId }
    const target = resolveSessionTarget(access, getCompanyId())
    if (target.kind === 'membership') signIn(sessionUser, { id: target.company.id, name: target.company.name }, () => null)
    if (target.kind === 'onboarding') navigate('/primeiros-passos', { replace: true })
    if (target.kind === 'adminHome' && !user.id) signIn(sessionUser, { id: '', name: '' }, () => null)
    if (target.kind !== 'adminCompany' || loadingCompanyId === target.companyId) return
    setLoadingCompanyId(target.companyId)
    fetchCompanyData(target.companyId)
      .then(data => signIn(sessionUser, { id: data.id, name: data.name }, () => null))
      .catch(failure => {
        if (GONE_COMPANY_STATUSES.includes(errorStatus(failure) ?? 0)) clearCompanyId()
        else setCompanyError(failure)
      })
      .finally(() => setLoadingCompanyId(null))
  }, [access, isError, companyError, sessionEnded, company.id, loadingCompanyId, user, signIn, signOut, navigate])

  if (!token) return <Navigate to="/login" state={{ from: location }} replace />
  if (!ready && ((isError && !sessionEnded) || companyError)) {
    return (
      <div className="grid min-h-dvh place-items-center bg-background px-4">
        <ErrorState
          error={companyError ?? error}
          title="Não foi possível carregar sua conta"
          onRetry={() => (companyError ? setCompanyError(null) : void refetch())}
        />
      </div>
    )
  }
  if (!ready) return <FullscreenLoader />

  return (
    <PermissionsProvider>
      <Outlet />
    </PermissionsProvider>
  )
}

function Home() {
  const { company } = useAuth()
  const { loading, hasPermission } = usePermission()
  const location = useLocation()
  if (!company.id) return <Navigate to={ADMIN_HOME} replace />
  if (loading) return <PageLoader />
  const target = landingDashboard(
    allowedDashboards(resource => hasPermission(resource, 'read')),
    readLastDashboard(company.id)
  )
  if (!target) return <NoDashboard />
  return <Navigate to={target.path} replace state={location.state} />
}

function Gate({
  resource,
  action = 'read',
  persist = false,
  children
}: {
  resource: string
  action?: 'read' | 'write'
  persist?: boolean
  children: ReactNode
}) {
  const params = useParams()
  const entityKey = persist ? 'page' : Object.values(params).join('/')
  return (
    <CompanyRequired>
      <PermissionGate requires={{ resource, action }}>
        <Fragment key={entityKey}>{children}</Fragment>
      </PermissionGate>
    </CompanyRequired>
  )
}

function CompanyRequired({ children }: { children: ReactNode }) {
  const { company } = useAuth()
  const { companyId } = useParams()
  if (!company.id && !companyId) return <Navigate to={ADMIN_HOME} replace />
  return <>{children}</>
}

function SuperAdminGate({ children }: { children: ReactNode }) {
  const { data: access, isLoading } = useMyAccess()
  if (isLoading) return <PageLoader />
  if (!access?.isSuperAdmin) return <AccessDenied description="Esta área é restrita a administradores da plataforma." />
  return <>{children}</>
}

function CompanySettingsRoute() {
  const { companyId } = useParams()
  return (
    <PermissionsProvider key={companyId} scopeCompanyId={companyId}>
      <PermissionGate requires={{ resource: 'settings', action: 'read' }}>
        <CompanySettingsLayout />
      </PermissionGate>
    </PermissionsProvider>
  )
}

export default function App() {
  return (
    <Suspense fallback={<FullscreenLoader />}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/login/google" element={<GoogleCallback />} />
        <Route path="/registro" element={<Register />} />
        <Route path="/esqueci-minha-senha" element={<ForgotPassword />} />
        <Route path="/recuperar-senha" element={<RecoverPassword />} />
        <Route path="/descadastrar" element={<Unsubscribe />} />
        <Route path="/primeiros-passos" element={<InitialSetup />} />
        <Route path="/primeiros-passos/criar-empresa" element={<NewCompany />} />
        <Route path="/primeiros-passos/aceitar-convite" element={<AcceptInvites />} />

        <Route element={<RequireSession />}>
          <Route
            path={ONBOARDING_PATH}
            element={
              <CompanyRequired>
                <Onboarding />
              </CompanyRequired>
            }
          />
          <Route element={<AppLayout />}>
            <Route path="/" element={<Home />} />
            <Route path={DASHBOARD_ROOT} element={<Home />} />
            <Route
              path="/dashboard/site"
              element={
                <Gate resource="settings">
                  <SiteDashboard />
                </Gate>
              }
            />
            <Route
              path="/dashboard/vendas"
              element={
                <Gate resource="crm">
                  <SalesDashboard />
                </Gate>
              }
            />
            <Route
              path="/dashboard/financeiro"
              element={
                <Gate resource="rentals">
                  <FinanceDashboard />
                </Gate>
              }
            />

            <Route
              path="/imoveis"
              element={
                <Gate resource="real-state">
                  <RealState />
                </Gate>
              }
            />
            <Route
              path="/imoveis/novo-imovel"
              element={
                <Gate resource="real-state" action="write">
                  <CreateRealState />
                </Gate>
              }
            />
            <Route
              path="/imoveis/chaves"
              element={
                <Gate resource="real-state">
                  <PropertyKeys />
                </Gate>
              }
            />
            <Route
              path="/imoveis/proprietarios"
              element={
                <Gate resource="real-state">
                  <Owners />
                </Gate>
              }
            />
            <Route
              path="/imoveis/proprietarios/novo"
              element={
                <Gate resource="real-state" action="write">
                  <OwnerForm />
                </Gate>
              }
            />
            <Route
              path="/imoveis/proprietarios/:id"
              element={
                <Gate resource="real-state">
                  <GetOwner />
                </Gate>
              }
            />
            <Route
              path="/imoveis/proprietarios/:id/editar"
              element={
                <Gate resource="real-state" action="write">
                  <OwnerForm />
                </Gate>
              }
            />
            <Route
              path="/imoveis/captacoes"
              element={
                <Gate resource="real-state">
                  <ListingIntakes />
                </Gate>
              }
            />
            <Route
              path="/imoveis/captacoes/:id"
              element={
                <Gate resource="real-state">
                  <GetListingIntake />
                </Gate>
              }
            />
            <Route
              path="/imoveis/:id"
              element={
                <Gate resource="real-state">
                  <GetRealState />
                </Gate>
              }
            />
            <Route
              path="/imoveis/:id/editar"
              element={
                <Gate resource="real-state" action="write">
                  <EditRealState />
                </Gate>
              }
            />

            <Route
              path="/clientes"
              element={
                <Gate resource="customers">
                  <ListCustomers />
                </Gate>
              }
            />
            <Route
              path="/clientes/novo-cliente"
              element={
                <Gate resource="customers" action="write">
                  <CreateCustomer />
                </Gate>
              }
            />
            <Route
              path="/clientes/:id"
              element={
                <Gate resource="customers">
                  <GetCustomer />
                </Gate>
              }
            />
            <Route
              path="/clientes/:id/editar"
              element={
                <Gate resource="customers" action="write">
                  <EditCustomer />
                </Gate>
              }
            />

            <Route
              path="/contratos"
              element={
                <Gate resource="rentals">
                  <ListRentContracts />
                </Gate>
              }
            />
            <Route
              path="/contratos/novo"
              element={
                <Gate resource="rentals" action="write">
                  <RentContractWizard />
                </Gate>
              }
            />
            <Route
              path="/contratos/:id"
              element={
                <Gate resource="rentals">
                  <RentContractForm />
                </Gate>
              }
            />
            <Route
              path="/contratos/:id/editar"
              element={
                <Gate resource="rentals" action="write">
                  <RentContractWizard mode="edit" />
                </Gate>
              }
            />
            <Route
              path="/financeiro"
              element={
                <Gate resource="rentals">
                  <Financeiro />
                </Gate>
              }
            />
            <Route
              path="/financeiro/recebimentos"
              element={
                <Gate resource="rentals">
                  <OnlineBilling />
                </Gate>
              }
            />
            <Route
              path="/financeiro/:id"
              element={
                <Gate resource="rentals">
                  <GetPayment />
                </Gate>
              }
            />

            <Route
              path="/documentos/modelos"
              element={
                <Gate resource="documents">
                  <DocumentModels />
                </Gate>
              }
            />
            <Route
              path="/documentos/modelos/novo"
              element={
                <Gate resource="documents" action="write">
                  <NewDocumentModel />
                </Gate>
              }
            />
            <Route
              path="/documentos/modelos/:id"
              element={
                <Gate resource="documents" action="write">
                  <EditDocumentModel />
                </Gate>
              }
            />
            <Route
              path="/documentos/modelos/:id/visualizar"
              element={
                <Gate resource="documents">
                  <PreviewModel />
                </Gate>
              }
            />

            <Route
              path="/crm"
              element={
                <Gate resource="crm">
                  <SalesFunnelBoard />
                </Gate>
              }
            />
            <Route
              path="/crm/relatorios"
              element={
                <Gate resource="crm">
                  <SalesReportsRedirect />
                </Gate>
              }
            />
            <Route
              path="/crm/configuracoes"
              element={
                <Gate resource="crm-team" action="write">
                  <SalesFunnelSettings />
                </Gate>
              }
            />
            <Route
              path="/crm/:id"
              element={
                <Gate resource="crm">
                  <SalesFunnelLeadDetails />
                </Gate>
              }
            />
            <Route
              path="/chat"
              element={
                <Gate resource="chat" persist>
                  <ChatPage />
                </Gate>
              }
            />
            <Route
              path="/chat/:id"
              element={
                <Gate resource="chat" persist>
                  <ChatPage />
                </Gate>
              }
            />

            <Route
              path="/missoes"
              element={
                <Gate resource="settings" action="write">
                  <Missions />
                </Gate>
              }
            />
            <Route
              path="/studio"
              element={
                <Gate resource="real-state">
                  <StudioVideos />
                </Gate>
              }
            />
            <Route
              path="/studio/novo"
              element={
                <Gate resource="real-state">
                  <NewStudioVideo />
                </Gate>
              }
            />
            <Route
              path="/blog"
              element={
                <Gate resource="blog">
                  <ListBlogPosts />
                </Gate>
              }
            />
            <Route
              path="/blog/novo"
              element={
                <Gate resource="blog" action="write">
                  <EditBlogPost />
                </Gate>
              }
            />
            <Route
              path="/blog/:id"
              element={
                <Gate resource="blog">
                  <EditBlogPost />
                </Gate>
              }
            />
            <Route
              path="/blog/:id/editar"
              element={
                <Gate resource="blog" action="write">
                  <EditBlogPost />
                </Gate>
              }
            />

            <Route path="/empresas" element={<ListCompanies />} />
            <Route path="/empresas/:companyId/configuracoes" element={<CompanySettingsRoute />}>
              <Route index element={<Navigate to="geral/informacoes" replace />} />
              <Route path="geral/informacoes" element={<GeneralSettings />} />
              <Route path="geral/marca-dagua" element={<Watermark />} />
              <Route path="site" element={<Navigate to={DEFAULT_SITE_SECTION} replace />} />
              <Route path="site/:section" element={<SiteSettings />} />
              <Route
                path="equipe"
                element={
                  <Gate resource="members">
                    <Team />
                  </Gate>
                }
              />
              <Route
                path="atividades"
                element={
                  <Gate resource="members">
                    <Activity />
                  </Gate>
                }
              />
              <Route path="integracoes" element={<Integrations />} />
              <Route path="integracoes/instagram" element={<InstagramSettings />} />
              <Route path="integracoes/whatsapp" element={<WhatsAppSettings />} />
              <Route path="integracoes/olx" element={<OlxSettings />} />
              <Route path="atendente-ia" element={<AttendantSettings />} />
              <Route
                path="assinaturas"
                element={
                  <Gate resource="subscriptions">
                    <Subscriptions />
                  </Gate>
                }
              />
            </Route>

            <Route path="/administracao" element={<Navigate to={ADMIN_HOME} replace />} />
            <Route
              path="/administracao/imobiliarias"
              element={
                <SuperAdminGate>
                  <AdminCompanies />
                </SuperAdminGate>
              }
            />
            <Route
              path="/administracao/agendamentos"
              element={
                <SuperAdminGate>
                  <AdminSchedules />
                </SuperAdminGate>
              }
            />
            <Route
              path="/administracao/saude"
              element={
                <SuperAdminGate>
                  <AdminHealth />
                </SuperAdminGate>
              }
            />
            <Route
              path="/administracao/vendas"
              element={
                <SuperAdminGate>
                  <AdminSales />
                </SuperAdminGate>
              }
            />
            <Route
              path="/administracao/ia"
              element={
                <SuperAdminGate>
                  <AdminAi />
                </SuperAdminGate>
              }
            />

            <Route path="/usuario" element={<GetUser />} />
            <Route path="/usuario/convites" element={<UserInvites />} />
            <Route path="/oauth/instagram/login" element={<InstagramOAuth />} />
            <Route path="*" element={<NotFound />} />
          </Route>
        </Route>
      </Routes>
    </Suspense>
  )
}
